import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { REGISTER_ROLES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { HandCoins, Loader2, CheckCircle2 } from "lucide-react";

const BG_URL =
  "https://images.unsplash.com/photo-1573765727997-e02883182ba7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxpc2xhbWljJTIwZ2VvbWV0cmljJTIwcGF0dGVybnxlbnwwfHx8fDE3ODczNzY1MjV8MA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "operator" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      if (data.status === "pending") {
        setSuccess(true);
      } else {
        setUser(data);
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4" data-testid="register-page">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG_URL})` }} />
      <div className="absolute inset-0 bg-emerald-950/70" />

      <div className="relative w-full max-w-md rounded-xl border border-white/40 bg-white/85 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.18)] backdrop-blur-xl">
        <div className="mb-6 flex items-center gap-3">
          <img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-14 w-14 rounded-xl bg-white object-contain p-1" />
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900 leading-tight">Registrasi Akun</h1>
            <p className="text-xs text-slate-500">Sistem Informasi LAZISNU Dawe</p>
          </div>
        </div>

        {success ? (
          <div className="text-center py-4" data-testid="register-success">
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-700" />
            <h2 className="font-heading text-lg font-bold text-slate-900">Pendaftaran Berhasil</h2>
            <p className="mt-2 text-sm text-slate-500">
              Akun Anda menunggu persetujuan admin. Anda dapat masuk setelah akun disetujui.
            </p>
            <Link to="/login">
              <Button data-testid="register-goto-login-button" className="mt-5 w-full bg-emerald-800 hover:bg-emerald-700 text-white">
                Kembali ke Halaman Masuk
              </Button>
            </Link>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div className="space-y-2">
            <Label htmlFor="name">Nama Lengkap</Label>
            <Input
              id="name"
              data-testid="register-name-input"
              placeholder="Nama lengkap"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email (Username)</Label>
            <Input
              id="email"
              data-testid="register-email-input"
              type="email"
              placeholder="nama@email.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Nomor WA</Label>
            <Input
              id="phone"
              data-testid="register-phone-input"
              placeholder="08xxxxxxxxxx"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Kata Sandi</Label>
            <Input
              id="password"
              data-testid="register-password-input"
              type="password"
              placeholder="Minimal 6 karakter"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Sebagai</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="register-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REGISTER_ROLES.map((r) => (
                  <SelectItem key={r.key} value={r.key} data-testid={`register-role-${r.key}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div data-testid="register-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}

          <Button
            data-testid="register-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-800 hover:bg-emerald-700 text-white"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Daftar"}
          </Button>
        </form>
        )}

        <p className="mt-5 text-center text-sm text-slate-500">
          Sudah punya akun?{" "}
          <Link to="/login" data-testid="login-link" className="font-semibold text-emerald-800 hover:underline">
            Masuk
          </Link>
        </p>
      </div>
    </div>
  );
}
